
    <template>
      <div :style="{ marginTop: '20px', paddingBottom: '80px' }">

        <div v-if="!loading">
          <form @submit.prevent="save_config">

            <div v-if="isEffectiveAdmin && tenant_type === 'CM'" class="d-flex align-items-center mb-3 alerts-global-row" data-field="ftp_enabled">
              <span class="alerts-global-label">Booking FTP feed</span>
              <b-form-select v-model="model.ftp_enabled" :options="boolean_options" style="width:100px"></b-form-select>
              <small class="text-muted ml-3">Login for the Coach Manager XML Writer feed. False rejects the writer at login; the customer should also stop their XML Writer service.</small>
            </div>

            <div v-if="tenant_type === 'FASTTRAK'" class="d-flex align-items-center mb-3 alerts-global-row">
              <span class="alerts-global-label">Fasttrak telematics actuals</span>
              <b-form-select v-model="model.fasttrak_actuals_enabled" :options="boolean_options" style="width:100px"></b-form-select>
              <small class="text-muted ml-3">Enqueue after scheduled end (+ delay below)</small>
            </div>
            <div v-if="tenant_type === 'FASTTRAK'" class="d-flex align-items-center mb-3 alerts-global-row">
              <span class="alerts-global-label">Fasttrak actuals delay (minutes after scheduled end)</span>
              <div style="min-width: 120px;">
                <b-form-input v-model="model.fasttrak_actuals_delay_minutes" type="text" :state="numericState('fasttrak_actuals_delay_minutes')" @input="validateNumeric('fasttrak_actuals_delay_minutes', $event, 0, 1440)"></b-form-input>
                <b-form-invalid-feedback :state="numericState('fasttrak_actuals_delay_minutes')">{{ numericFeedback('fasttrak_actuals_delay_minutes') }}</b-form-invalid-feedback>
              </div>
              <small class="text-muted ml-3">Falls back to actuals_processing_delay_in_minutes if unset</small>
            </div>

            <!-- Booking Processing Rules -->
            <b-card no-body class="alert-section-card mb-2" data-section="isBookingProcessingVisible">
              <b-card-header class="alert-section-header d-flex align-items-center" @click="toggle_div(toggle, 'isBookingProcessingVisible')">
                <span class="alert-chevron mr-2" :class="{ open: toggle.isBookingProcessingVisible }">&#9654;</span>
                <span class="alert-section-title">Booking Processing Rules</span>
              </b-card-header>
              <b-collapse id="collapse-isBookingProcessingVisible" :visible="!!toggle.isBookingProcessingVisible">
                <b-card-body>
                  <SubsectionGroup label="Include / Exclude Rules" :visible="!!toggle.grpBookingIncludeExclude" @toggle="toggle_div(toggle, 'grpBookingIncludeExclude')">
                    <tr v-if="tenant_type=='CM'" data-field="actuals_skip_contract_bookings">
                      <td class="alert-label">Skip Contract Bookings</td>
                      <td><b-form-select v-model="model.actuals_skip_contract_bookings" :options="boolean_options" style="width:100px"></b-form-select></td>
                    </tr>
                    <tr v-if="tenant_type=='CM'" data-field="actuals_exclude_non_diary_bookings">
                      <td class="alert-label">Exclude Non Diary Bookings</td>
                      <td><b-form-select v-model="model.actuals_exclude_non_diary_bookings" :options="boolean_options" style="width:100px"></b-form-select></td>
                    </tr>
                    <tr v-if="tenant_type=='TBN'" data-field="actuals_skip_bookings_without_vehicles">
                      <td class="alert-label">Skip Bookings Without Vehicles Assigned</td>
                      <td><b-form-select v-model="model.actuals_skip_bookings_without_vehicles" :options="boolean_options" style="width:100px"></b-form-select></td>
                    </tr>
                    <tr v-if="tenant_type == 'CM'" data-field="actuals_ignore_booking_if_vehicle_is_missing">
                      <td class="alert-label">Ignore Booking If Missing Vehicle</td>
                      <td><b-form-select v-model="model.actuals_ignore_booking_if_vehicle_is_missing" :options="boolean_options" style="width:100px"></b-form-select></td>
                    </tr>
                  </SubsectionGroup>

                  <SubsectionGroup label="Booking Type &amp; ID Filters" :visible="!!toggle.grpBookingTypeIdFilters" @toggle="toggle_div(toggle, 'grpBookingTypeIdFilters')">
                    <tr v-if="tenant_type=='CM'" data-field="actuals_excluded_booking_types">
                      <td class="alert-label">Excluded Booking Types</td>
                      <td>
                        <!-- Backend does an exact comma-split membership check — plain exclusion
                             list, no `*`/`-` wildcard support. -->
                        <FilterSettingEditor
                          v-model="model.actuals_excluded_booking_types"
                          fieldKey="actuals_excluded_booking_types"
                          variant="list"
                          :allow-raw-edit="isEffectiveAdmin"
                          @validation="onFilterValidation"
                        />
                      </td>
                    </tr>
                    <tr v-if="tenant_type=='TBN'" data-field="actuals_scheduled_services_filter">
                      <td class="alert-label">Scheduled Services Filter</td>
                      <td><b-form-input v-model="model.actuals_scheduled_services_filter" type="text"></b-form-input></td>
                    </tr>
                    <tr v-if="tenant_type=='CM'" data-field="actuals_client_id_filter">
                      <td class="alert-label">Client ID Filter</td>
                      <td>
                        <FilterSettingEditor
                          v-model="model.actuals_client_id_filter"
                          fieldKey="actuals_client_id_filter"
                          variant="wildcard"
                          :allow-raw-edit="isEffectiveAdmin"
                          @validation="onFilterValidation"
                        />
                      </td>
                    </tr>
                    <tr v-if="tenant_type=='CM'" data-field="actuals_supported_client_ids">
                      <td class="alert-label">Supported Client IDs</td>
                      <td><b-form-input v-model="model.actuals_supported_client_ids" type="text"></b-form-input></td>
                    </tr>
                  </SubsectionGroup>

                  <SubsectionGroup label="Vehicle Filters" :visible="!!toggle.grpVehicleFilters" @toggle="toggle_div(toggle, 'grpVehicleFilters')">
                    <tr data-field="actuals_excluded_booking_vehicles">
                      <td class="alert-label">Excluded Booking Vehicles</td>
                      <td>
                        <!-- Backend does an exact comma-split membership check (case-insensitive) —
                             plain exclusion list, no `*`/`-` wildcard support. -->
                        <FilterSettingEditor
                          v-model="model.actuals_excluded_booking_vehicles"
                          fieldKey="actuals_excluded_booking_vehicles"
                          variant="list"
                          :allow-raw-edit="isEffectiveAdmin"
                          @validation="onFilterValidation"
                        />
                      </td>
                    </tr>
                    <tr data-field="actuals_vehicle_type_filter">
                      <td class="alert-label">Supported Actuals Vehicle Types</td>
                      <td>
                        <FilterSettingEditor
                          v-model="model.actuals_vehicle_type_filter"
                          fieldKey="actuals_vehicle_type_filter"
                          variant="wildcard"
                          :allow-raw-edit="isEffectiveAdmin"
                          @validation="onFilterValidation"
                        />
                        <small class="text-muted d-block">Unsupported types still appear on actuals page as skipped</small>
                      </td>
                    </tr>
                  </SubsectionGroup>

                  <SubsectionGroup label="Base Filters" :visible="!!toggle.grpBaseFilters" @toggle="toggle_div(toggle, 'grpBaseFilters')">
                    <tr v-if="tenant_type=='CM'" data-field="actuals_excluded_booking_bases">
                      <td class="alert-label">Excluded Booking Bases</td>
                      <td>
                        <!-- Backend does an exact comma-split membership check (case-insensitive) —
                             plain exclusion list, no `*`/`-` wildcard support. -->
                        <FilterSettingEditor
                          v-model="model.actuals_excluded_booking_bases"
                          fieldKey="actuals_excluded_booking_bases"
                          variant="list"
                          :allow-raw-edit="isEffectiveAdmin"
                          @validation="onFilterValidation"
                        />
                      </td>
                    </tr>
                    <tr v-if="tenant_type=='CM'" data-field="actuals_supported_booking_bases">
                      <td class="alert-label">Supported Booking Bases</td>
                      <td><b-form-input v-model="model.actuals_supported_booking_bases" type="text"></b-form-input></td>
                    </tr>
                  </SubsectionGroup>

                  <SubsectionGroup v-if="tenant_type === 'CM'" label="Feed Issue Alerts" :visible="!!toggle.grpFeedIssueAlerts" @toggle="toggle_div(toggle, 'grpFeedIssueAlerts')">
                    <tr data-field="actuals_booking_feed_issues_email_recipients">
                      <td class="alert-label">Feed Issues Email Recipient</td>
                      <td><b-form-input v-model="model.actuals_booking_feed_issues_email_recipients" type="text"></b-form-input></td>
                    </tr>
                    <tr data-field="actuals_booking_feed_issues_hours_since">
                      <td class="alert-label">Feed Issues Hours Since Updates To Alert On</td>
                      <td>
                        <b-form-input v-model="model.actuals_booking_feed_issues_hours_since" type="text" :state="numericState('actuals_booking_feed_issues_hours_since')" @input="validateNumeric('actuals_booking_feed_issues_hours_since', $event, 1, 168)"></b-form-input>
                        <b-form-invalid-feedback :state="numericState('actuals_booking_feed_issues_hours_since')">{{ numericFeedback('actuals_booking_feed_issues_hours_since') }}</b-form-invalid-feedback>
                      </td>
                    </tr>
                    <tr data-field="booking_flow_skip_privates">
                      <td class="alert-label">Feed Issues - Ignore Private Hires</td>
                      <td><b-form-select v-model="model.booking_flow_skip_privates" :options="boolean_options" style="width:100px"></b-form-select></td>
                    </tr>
                    <tr data-field="booking_flow_skip_contracts">
                      <td class="alert-label">Feed Issues - Ignore Contracts</td>
                      <td><b-form-select v-model="model.booking_flow_skip_contracts" :options="boolean_options" style="width:100px"></b-form-select></td>
                    </tr>
                  </SubsectionGroup>

                  <b-card v-if="isEffectiveAdmin" no-body class="alert-section-card mb-0 mt-3" data-section="isBookingProcessingAdvVisible">
                    <b-card-header class="alert-section-header d-flex align-items-center" @click="toggle_div(toggle, 'isBookingProcessingAdvVisible')">
                      <span class="alert-chevron mr-2" :class="{ open: toggle.isBookingProcessingAdvVisible }">&#9654;</span>
                      <span class="alert-section-title">Advanced</span>
                    </b-card-header>
                    <b-collapse id="collapse-isBookingProcessingAdvVisible" :visible="!!toggle.isBookingProcessingAdvVisible">
                      <b-card-body>
                        <SubsectionGroup label="Processing Behavior" :visible="!!toggle.grpProcessingBehavior" @toggle="toggle_div(toggle, 'grpProcessingBehavior')">
                          <tr v-if="tenant_type=='CM'" data-field="booking_replace_with_eld_values">
                            <td class="alert-label">Replace Vehicle/Driver With Telematics Values</td>
                            <td><b-form-select v-model="model.booking_replace_with_eld_values" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr v-if="tenant_type=='CM'" data-field="actuals_skip_duplicate_booking_updates">
                            <td class="alert-label">Skip Duplicate Bookings Updates</td>
                            <td><b-form-select v-model="model.actuals_skip_duplicate_booking_updates" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr v-if="tenant_type=='CM'" data-field="actuals_supports_booking_by_vehicle_company">
                            <td class="alert-label">Supports Booking By Vehicle Company</td>
                            <td><b-form-select v-model="model.actuals_supports_booking_by_vehicle_company" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr v-if="tenant_type === 'CM'" data-field="actuals_default_invalid_start_times_enabled">
                            <td class="alert-label">Default Invalid Start/Finish Times</td>
                            <td>
                              <b-form-select v-model="model.actuals_default_invalid_start_times_enabled" :options="boolean_options" style="width:100px"></b-form-select>
                              <small class="text-muted d-block">Midnight (12:00) start defaults to 1 hr before position; finish defaults to 1 hr after back or leave</small>
                            </td>
                          </tr>
                          <tr data-field="actuals_driver_hos_required_enabled">
                            <td class="alert-label">Driver HOS Required</td>
                            <td><b-form-select v-model="model.actuals_driver_hos_required_enabled" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr v-if="tenant_type=='CM'" data-field="actuals_max_days_ago">
                            <td class="alert-label">Max Days Ago To Process Bookings</td>
                            <td>
                              <b-form-input v-model="model.actuals_max_days_ago" type="text" :state="numericState('actuals_max_days_ago')" @input="validateNumeric('actuals_max_days_ago', $event, 0, 365)"></b-form-input>
                              <b-form-invalid-feedback :state="numericState('actuals_max_days_ago')">{{ numericFeedback('actuals_max_days_ago') }}</b-form-invalid-feedback>
                            </td>
                          </tr>
                        </SubsectionGroup>

                        <SubsectionGroup v-if="tenant_type=='TBN'" label="Booking Refresh &amp; Sync" :visible="!!toggle.grpBookingRefreshSync" @toggle="toggle_div(toggle, 'grpBookingRefreshSync')">
                          <tr data-field="actuals_force_booking_updates_enabled">
                            <td class="alert-label">Force Booking Updates Enabled</td>
                            <td><b-form-select v-model="model.actuals_force_booking_updates_enabled" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr data-field="actuals_refresh_bookings_disabled">
                            <td class="alert-label">Refresh Bookings Disabled</td>
                            <td><b-form-select v-model="model.actuals_refresh_bookings_disabled" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr data-field="actuals_refresh_bookings_nightly_disabled">
                            <td class="alert-label">Refresh Bookings Nightly Disabled</td>
                            <td><b-form-select v-model="model.actuals_refresh_bookings_nightly_disabled" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr data-field="actuals_refresh_booking_days_in_future">
                            <td class="alert-label">Days In The Future</td>
                            <td>
                              <b-form-input v-model="model.actuals_refresh_booking_days_in_future" type="text" :state="numericState('actuals_refresh_booking_days_in_future')" @input="validateNumeric('actuals_refresh_booking_days_in_future', $event, 0, 365)"></b-form-input>
                              <b-form-invalid-feedback :state="numericState('actuals_refresh_booking_days_in_future')">{{ numericFeedback('actuals_refresh_booking_days_in_future') }}</b-form-invalid-feedback>
                            </td>
                          </tr>
                          <tr data-field="actuals_refresh_bookings_minutes_past_the_hour">
                            <td class="alert-label">Refresh Minutes Past The Hour</td>
                            <td>
                              <b-form-input v-model="model.actuals_refresh_bookings_minutes_past_the_hour" type="text" :state="numericState('actuals_refresh_bookings_minutes_past_the_hour')" @input="validateNumeric('actuals_refresh_bookings_minutes_past_the_hour', $event, 0, 59)"></b-form-input>
                              <b-form-invalid-feedback :state="numericState('actuals_refresh_bookings_minutes_past_the_hour')">{{ numericFeedback('actuals_refresh_bookings_minutes_past_the_hour') }}</b-form-invalid-feedback>
                            </td>
                          </tr>
                        </SubsectionGroup>
                      </b-card-body>
                    </b-collapse>
                  </b-card>
                </b-card-body>
              </b-collapse>
            </b-card>

            <!-- Vehicle & Driver Sync Settings -->
            <b-card v-if="tenant_type=='TBN'" no-body class="alert-section-card mb-2" data-section="isVehicleDriverSyncVisible">
              <b-card-header class="alert-section-header d-flex align-items-center" @click="toggle_div(toggle, 'isVehicleDriverSyncVisible')">
                <span class="alert-chevron mr-2" :class="{ open: toggle.isVehicleDriverSyncVisible }">&#9654;</span>
                <span class="alert-section-title">Vehicle &amp; Driver Sync Settings</span>
              </b-card-header>
              <b-collapse id="collapse-isVehicleDriverSyncVisible" :visible="!!toggle.isVehicleDriverSyncVisible">
                <b-card-body>
                  <table width="100%" class="alert-settings-table" cellpadding="8">
                    <tr data-field="tbn_sync_drivers_skip">
                      <td class="alert-label">Sync Drivers Disabled</td>
                      <td><b-form-select v-model="model.tbn_sync_drivers_skip" :options="boolean_options" style="width:100px"></b-form-select></td>
                    </tr>
                    <tr data-field="tbn_sync_drivers_basic_enabled">
                      <td class="alert-label">Sync Drivers Basic Enabled</td>
                      <td><b-form-select v-model="model.tbn_sync_drivers_basic_enabled" :options="boolean_options" style="width:100px"></b-form-select></td>
                    </tr>
                    <tr data-field="tbn_sync_vehicles_skip">
                      <td class="alert-label">Sync Vehicles Disabled</td>
                      <td><b-form-select v-model="model.tbn_sync_vehicles_skip" :options="boolean_options" style="width:100px"></b-form-select></td>
                    </tr>
                    <tr data-field="tbn_sync_add_eld_vehicles">
                      <td class="alert-label">Add Missing Telematics Vehicles To TBN</td>
                      <td><b-form-select v-model="model.tbn_sync_add_eld_vehicles" :options="boolean_options" style="width:100px"></b-form-select></td>
                    </tr>
                    <tr data-field="tbn_sync_mismatch_report_recipients">
                      <td class="alert-label" style="text-align:left; width:auto">Telematics Mismatch Report Recipients</td>
                      <td>
                        <b-form-input v-model="model.tbn_sync_mismatch_report_recipients" type="text"></b-form-input>
                        <small class="text-muted d-block">Comma-separated emails to notify with drivers/vehicles found in TBN with no telematics match</small>
                      </td>
                    </tr>
                    <tr data-field="tbn_sync_excluded_drivers">
                      <td class="alert-label" style="text-align:left; width:auto">Excluded Drivers (Name/ID)</td>
                      <td>
                        <b-form-input v-model="model.tbn_sync_excluded_drivers" type="text"></b-form-input>
                        <small class="text-muted d-block">Comma-separated TBN driver names or IDs to skip entirely during sync/matching</small>
                      </td>
                    </tr>
                    <tr data-field="tbn_sync_excluded_vehicles">
                      <td class="alert-label" style="text-align:left; width:auto">Excluded Vehicles (Name/ID)</td>
                      <td>
                        <b-form-input v-model="model.tbn_sync_excluded_vehicles" type="text"></b-form-input>
                        <small class="text-muted d-block">Comma-separated TBN vehicle unit numbers or IDs to skip entirely during sync/matching</small>
                      </td>
                    </tr>
                    <tr data-field="tbn_sync_vehicle_type_mapping">
                      <td class="alert-label" style="text-align:left; width:auto">New Vehicle Type Mapping</td>
                      <td>
                        <small class="text-muted d-block mb-1">Telematics tag -> TBN vehicle type name (must match exactly as it appears in TBN), checked top to bottom, used when creating a new TBN vehicle for a telematics vehicle with no TBN match</small>
                        <JsonObjectListEditor v-model="model.tbn_sync_vehicle_type_mapping" fieldKey="tbn_sync_vehicle_type_mapping" @validation="onJsonValidation" :allow-raw-json="isEffectiveAdmin" :columns="vehicle_type_mapping_columns"/>
                      </td>
                    </tr>
                    <tr data-field="tbn_sync_vehicle_type_mapping_default">
                      <td class="alert-label" style="text-align:left; width:auto">Default Vehicle Type Name</td>
                      <td>
                        <b-form-input v-model="model.tbn_sync_vehicle_type_mapping_default" type="text"></b-form-input>
                        <small class="text-muted d-block">TBN vehicle type name to use when a new vehicle's tags don't match the mapping above</small>
                      </td>
                    </tr>
                    <tr data-field="tbn_sync_vehicle_yard_mapping">
                      <td class="alert-label" style="text-align:left; width:auto">New Vehicle Yard Mapping</td>
                      <td>
                        <small class="text-muted d-block mb-1">Telematics tag -> TBN yard name (must match exactly as it appears in TBN), checked top to bottom, used when creating a new TBN vehicle for a telematics vehicle with no TBN match</small>
                        <JsonObjectListEditor v-model="model.tbn_sync_vehicle_yard_mapping" fieldKey="tbn_sync_vehicle_yard_mapping" @validation="onJsonValidation" :allow-raw-json="isEffectiveAdmin" :columns="vehicle_yard_mapping_columns"/>
                      </td>
                    </tr>
                    <tr data-field="tbn_sync_vehicle_yard_mapping_default">
                      <td class="alert-label" style="text-align:left; width:auto">Default Yard Name</td>
                      <td>
                        <b-form-input v-model="model.tbn_sync_vehicle_yard_mapping_default" type="text"></b-form-input>
                        <small class="text-muted d-block">TBN yard name to use when a new vehicle's tags don't match the mapping above</small>
                      </td>
                    </tr>
                  </table>
                  <SubsectionGroup label="FleetInt Driver Logins" :visible="!!toggle.grpFleetIntDriverLogins" @toggle="toggle_div(toggle, 'grpFleetIntDriverLogins')">
                    <tr data-field="tbn_sync_fleetint_drivers_enabled">
                      <td class="alert-label" style="text-align:left; width:auto">Create FleetInt Logins For TBN Drivers</td>
                      <td>
                        <b-form-select v-model="model.tbn_sync_fleetint_drivers_enabled" :options="boolean_options" style="width:100px"></b-form-select>
                        <small class="text-muted d-block">Nightly sync of this tenant's TBN drivers into FleetInt user accounts (role driver, username = the driver's TBN email) so they can log in with their TBN credentials</small>
                      </td>
                    </tr>
                    <tr data-field="tbn_sync_fleetint_drivers_employment_statuses">
                      <td class="alert-label" style="text-align:left; width:auto">Included Employment Statuses</td>
                      <td>
                        <b-form-input v-model="model.tbn_sync_fleetint_drivers_employment_statuses" type="text" placeholder="Full Time, Part Time, Casual"></b-form-input>
                        <small class="text-muted d-block">Comma-separated TBN employment statuses that get a FleetInt login, exactly as they appear in TBN. Defaults to Full Time, Part Time, Casual</small>
                      </td>
                    </tr>
                    <tr data-field="tbn_sync_fleetint_drivers_required_qualifications">
                      <td class="alert-label" style="text-align:left; width:auto">Required Driver Qualifications</td>
                      <td>
                        <b-form-input v-model="model.tbn_sync_fleetint_drivers_required_qualifications" type="text" placeholder="e.g. Motorcoach"></b-form-input>
                        <small class="text-muted d-block">Comma-separated TBN qualification descriptions -- a driver needs any one of them to get a FleetInt login. Leave empty to give every included driver a login</small>
                      </td>
                    </tr>
                    <tr data-field="tbn_sync_fleetint_drivers_remove_unqualified">
                      <td class="alert-label" style="text-align:left; width:auto">Remove Drivers No Longer In TBN</td>
                      <td>
                        <b-form-select v-model="model.tbn_sync_fleetint_drivers_remove_unqualified" :options="boolean_options" style="width:100px"></b-form-select>
                        <small class="text-muted d-block">Deletes FleetInt driver accounts the sync no longer finds as qualifying TBN drivers. Leave off if any driver logins are managed by hand</small>
                      </td>
                    </tr>
                    <tr data-field="tbn_sync_fleetint_drivers_company_names">
                      <td class="alert-label" style="text-align:left; width:auto">TBN Driver Company Names</td>
                      <td>
                        <b-form-input v-model="model.tbn_sync_fleetint_drivers_company_names" type="text"></b-form-input>
                        <small class="text-muted d-block">Only needed when TBN returns drivers from sibling companies that this tenant's TBN company ID doesn't filter out -- comma-separated company names exactly as they appear on the TBN driver record</small>
                      </td>
                    </tr>
                  </SubsectionGroup>
                </b-card-body>
              </b-collapse>
            </b-card>

            <!-- Actuals Processing Rules -->
            <b-card no-body class="alert-section-card mb-2" data-section="isActualsProcessingVisible">
              <b-card-header class="alert-section-header d-flex align-items-center" @click="toggle_div(toggle, 'isActualsProcessingVisible')">
                <span class="alert-chevron mr-2" :class="{ open: toggle.isActualsProcessingVisible }">&#9654;</span>
                <span class="alert-section-title">Actuals Processing Rules</span>
                <div class="ml-auto" @click.stop>
                  <b-btn v-if="isEffectiveAdmin" variant="outline-secondary" size="sm" class="mr-2" @click="confirmDefaults('Set default values for Actuals Processing Rules? This will overwrite current settings.', apply_actuals_defaults)">Set Defaults</b-btn>
                  <b-form-select v-model="model.actuals_enabled" :options="boolean_options" style="width:100px"></b-form-select>
                </div>
              </b-card-header>
              <b-collapse id="collapse-isActualsProcessingVisible" :visible="!!toggle.isActualsProcessingVisible">
                <b-card-body>
                  <SubsectionGroup label="Include / Exclude Rules" :visible="!!toggle.grpActualsIncludeExclude" @toggle="toggle_div(toggle, 'grpActualsIncludeExclude')">
                    <tr v-if="tenant_type=='TBN'" data-field="actuals_skip_scheduled_services">
                      <td class="alert-label">Skip Scheduled Services</td>
                      <td><b-form-select v-model="model.actuals_skip_scheduled_services" :options="boolean_options" style="width:100px"></b-form-select></td>
                    </tr>
                    <tr v-if="tenant_type == 'TBN'" data-field="actuals_base_config">
                      <td class="alert-label">Actuals Base Config</td>
                      <td>
                        <JsonObjectListEditor v-model="model.actuals_base_config" fieldKey="actuals_base_config" @validation="onJsonValidation" :allow-raw-json="isEffectiveAdmin" :columns="base_config_columns"/>
                        <b-form-invalid-feedback :state="jsonState('actuals_base_config')">{{ jsonFeedback('actuals_base_config') }}</b-form-invalid-feedback>
                        <small class="text-muted d-block">Only process actuals for these bases</small>
                      </td>
                    </tr>
                    <tr v-if="tenant_type === 'CM'" data-field="actuals_contract_id_filter">
                      <td class="alert-label">Contract ID Filter</td>
                      <td>
                        <FilterSettingEditor
                          v-model="model.actuals_contract_id_filter"
                          fieldKey="actuals_contract_id_filter"
                          variant="wildcard"
                          :allow-raw-edit="isEffectiveAdmin"
                          @validation="onFilterValidation"
                        />
                      </td>
                    </tr>
                  </SubsectionGroup>

                  <SubsectionGroup label="Processing Timing" :visible="!!toggle.grpProcessingTiming" @toggle="toggle_div(toggle, 'grpProcessingTiming')">
                    <tr v-if="tenant_type=='CM'" data-field="actuals_processing_delay_in_minutes">
                      <td class="alert-label">Minutes After Finish Time To Process Actuals</td>
                      <td>
                        <b-form-input v-model="model.actuals_processing_delay_in_minutes" type="text" :state="numericState('actuals_processing_delay_in_minutes')" @input="validateNumeric('actuals_processing_delay_in_minutes', $event, 0, 1440)"></b-form-input>
                        <b-form-invalid-feedback :state="numericState('actuals_processing_delay_in_minutes')">{{ numericFeedback('actuals_processing_delay_in_minutes') }}</b-form-invalid-feedback>
                      </td>
                    </tr>
                    <tr v-if="tenant_type=='TBN'" data-field="actuals_process_by_yard">
                      <td class="alert-label">Process Actuals By Yard</td>
                      <td><b-form-select v-model="model.actuals_process_by_yard" :options="boolean_options" style="width:100px"></b-form-select></td>
                    </tr>
                    <tr v-if="tenant_type == 'CM'" data-field="actuals_process_days_ago_limit">
                      <td class="alert-label">Process Days Ago Limit</td>
                      <td>
                        <b-form-input v-model="model.actuals_process_days_ago_limit" type="text" :state="numericState('actuals_process_days_ago_limit')" @input="validateNumeric('actuals_process_days_ago_limit', $event, 30, 360)"></b-form-input>
                        <b-form-invalid-feedback :state="numericState('actuals_process_days_ago_limit')">{{ numericFeedback('actuals_process_days_ago_limit') }}</b-form-invalid-feedback>
                      </td>
                    </tr>
                  </SubsectionGroup>

                  <SubsectionGroup label="Display &amp; View Filters" :visible="!!toggle.grpDisplayViewFilters" @toggle="toggle_div(toggle, 'grpDisplayViewFilters')">
                    <tr v-if="tenant_type=='TBN'" data-field="actuals_hide_vehicle_missing_errors_web">
                      <td class="alert-label">Hide Missing Vehicles Errors On Bookings Page</td>
                      <td><b-form-select v-model="model.actuals_hide_vehicle_missing_errors_web" :options="boolean_options" style="width:100px"></b-form-select></td>
                    </tr>
                    <tr data-field="actuals_filter_bookings_view_by_company">
                      <td class="alert-label">Filter Bookings By Company</td>
                      <td><b-form-select v-model="model.actuals_filter_bookings_view_by_company" :options="boolean_options" style="width:100px"></b-form-select></td>
                    </tr>
                    <tr data-field="actuals_date_time_format">
                      <td class="alert-label">Date Time Format</td>
                      <td><b-form-input v-model="model.actuals_date_time_format" type="text"></b-form-input></td>
                    </tr>
                  </SubsectionGroup>

                  <SubsectionGroup v-if="tenant_type == 'TBN'" label="Outbound to TBN" :visible="!!toggle.grpOutboundToTbn" @toggle="toggle_div(toggle, 'grpOutboundToTbn')">
                    <tr data-field="actuals_outbound_enabled">
                      <td class="alert-label">Send Actuals To TBN Enabled</td>
                      <td><b-form-select v-model="model.actuals_outbound_enabled" :options="boolean_options" style="width:100px"></b-form-select></td>
                    </tr>
                    <tr data-field="actuals_outbound_base_config">
                      <td class="alert-label">Outbound Base Config</td>
                      <td>
                        <JsonObjectListEditor v-model="model.actuals_outbound_base_config" fieldKey="actuals_outbound_base_config" @validation="onJsonValidation" :allow-raw-json="isEffectiveAdmin" :columns="base_config_columns"/>
                        <b-form-invalid-feedback :state="jsonState('actuals_outbound_base_config')">{{ jsonFeedback('actuals_outbound_base_config') }}</b-form-invalid-feedback>
                        <small class="text-muted d-block">Only send actuals to TBN for these bases</small>
                      </td>
                    </tr>
                    <tr data-field="actuals_outbound_auto_verify_config">
                      <td class="alert-label">Outbound Auto Verify Config</td>
                      <td>
                        <JsonObjectListEditor v-model="model.actuals_outbound_auto_verify_config" fieldKey="actuals_outbound_auto_verify_config" @validation="onJsonValidation" :allow-raw-json="isEffectiveAdmin" :columns="auto_verify_columns"/>
                        <b-form-invalid-feedback :state="jsonState('actuals_outbound_auto_verify_config')">{{ jsonFeedback('actuals_outbound_auto_verify_config') }}</b-form-invalid-feedback>
                      </td>
                    </tr>
                    <tr data-field="actuals_outbound_bookings_disabled">
                      <td class="alert-label">Outbound Booking Disabled</td>
                      <td><b-form-select v-model="model.actuals_outbound_bookings_disabled" :options="boolean_options" style="width:100px"></b-form-select></td>
                    </tr>
                    <tr data-field="actuals_outbound_scheduled_services_disabled">
                      <td class="alert-label">Outbound Scheduled Services Disabled</td>
                      <td><b-form-select v-model="model.actuals_outbound_scheduled_services_disabled" :options="boolean_options" style="width:100px"></b-form-select></td>
                    </tr>
                    <tr v-if="user.tenant == 'dattco-sb-815y467h'" data-field="actuals_yard_offset_config">
                      <td class="alert-label">Yard Clock In/Out Offsets</td>
                      <td>
                        <JsonObjectListEditor v-model="model.actuals_yard_offset_config" fieldKey="actuals_yard_offset_config" @validation="onJsonValidation" :allow-raw-json="isEffectiveAdmin" :columns="yard_offset_columns"/>
                        <b-form-invalid-feedback :state="jsonState('actuals_yard_offset_config')">{{ jsonFeedback('actuals_yard_offset_config') }}</b-form-invalid-feedback>
                        <small class="text-muted d-block">Adjust actual clock-in/out times</small>
                      </td>
                    </tr>
                  </SubsectionGroup>

                  <b-card v-if="isEffectiveAdmin" no-body class="alert-section-card mb-0 mt-3" data-section="isActualsProcessingAdvVisible">
                    <b-card-header class="alert-section-header d-flex align-items-center" @click="toggle_div(toggle, 'isActualsProcessingAdvVisible')">
                      <span class="alert-chevron mr-2" :class="{ open: toggle.isActualsProcessingAdvVisible }">&#9654;</span>
                      <span class="alert-section-title">Advanced</span>
                    </b-card-header>
                    <b-collapse id="collapse-isActualsProcessingAdvVisible" :visible="!!toggle.isActualsProcessingAdvVisible">
                      <b-card-body>
                        <SubsectionGroup v-if="tenant_type === 'TBN' || tenant_type === 'CM'" label="Actuals V2" :visible="!!toggle.grpActualsV2" @toggle="toggle_div(toggle, 'grpActualsV2')">
                          <tr data-field="actuals_v2_enabled">
                            <td class="alert-label">V2 Enabled (live)</td>
                            <td>
                              <b-form-select v-model="model.actuals_v2_enabled" :options="boolean_options" style="width:100px"></b-form-select>
                              <small class="text-muted d-block">Use advanced HOS logic for eligible bookings</small>
                            </td>
                          </tr>
                        </SubsectionGroup>

                        <SubsectionGroup v-if="tenant_type === 'TBN'" label="Time Calculation (TBN)" :visible="!!toggle.grpTimeCalcTbn" @toggle="toggle_div(toggle, 'grpTimeCalcTbn')">
                          <tr data-field="actuals_include_off_duty_assigned_time">
                            <td class="alert-label">Include Off Duty / Assigned To Vehicle Times In Actuals</td>
                            <td><b-form-select v-model="model.actuals_include_off_duty_assigned_time" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr data-field="actuals_fall_back_to_vehicle_times">
                            <td class="alert-label">Fall Back To Vehicle Times</td>
                            <td><b-form-select v-model="model.actuals_fall_back_to_vehicle_times" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr data-field="actuals_use_spot_time_as_pickup_time">
                            <td class="alert-label">Use Spot Time As Pickup Time</td>
                            <td><b-form-select v-model="model.actuals_use_spot_time_as_pickup_time" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr data-field="actuals_use_depart_time_as_dropoff_time">
                            <td class="alert-label">Use Depart Time As Dropoff Time</td>
                            <td><b-form-select v-model="model.actuals_use_depart_time_as_dropoff_time" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr data-field="actuals_fall_back_to_vehicle_times_scheduled_services">
                            <td class="alert-label">Fall Back To Vehicle Time (Scheduled Services)</td>
                            <td><b-form-select v-model="model.actuals_fall_back_to_vehicle_times_scheduled_services" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr data-field="actuals_use_planned_driver_start_time">
                            <td class="alert-label">Use Planned Driver Start Time</td>
                            <td><b-form-select v-model="model.actuals_use_planned_driver_start_time" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr data-field="actuals_use_planned_driver_start_time_scheduled_services">
                            <td class="alert-label">Use Planned Driver Start Time (Scheduled Services)</td>
                            <td><b-form-select v-model="model.actuals_use_planned_driver_start_time_scheduled_services" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr data-field="actuals_only_live_hours">
                            <td class="alert-label">Only Populate Live Hours (No Dead, Wait, Driving)</td>
                            <td><b-form-select v-model="model.actuals_only_live_hours" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr data-field="actuals_multiday_flat_hours">
                            <td class="alert-label">Flat Hours For Multiday Middle Days</td>
                            <td>
                              <b-form-input v-model="model.actuals_multiday_flat_hours" type="text" :state="numericState('actuals_multiday_flat_hours')" @input="validateNumeric('actuals_multiday_flat_hours', $event, 0, 24)"></b-form-input>
                              <b-form-invalid-feedback :state="numericState('actuals_multiday_flat_hours')">{{ numericFeedback('actuals_multiday_flat_hours') }}</b-form-invalid-feedback>
                            </td>
                          </tr>
                        </SubsectionGroup>

                        <SubsectionGroup v-if="tenant_type === 'TBN'" label="Other Assignments (TBN)" :visible="!!toggle.grpOtherAssignmentsTbn" @toggle="toggle_div(toggle, 'grpOtherAssignmentsTbn')">
                          <tr data-field="actuals_create_other_assignments_between_shifts">
                            <td class="alert-label">Create Other Assignments Between Shifts</td>
                            <td>
                              <b-form-select v-model="model.actuals_create_other_assignments_between_shifts" :options="boolean_options" style="width:100px"></b-form-select>
                              <small class="text-muted d-block">Create TBN Other assignments for driver/vehicle time between consecutive dispatches. Scheduled services only &mdash; charters and other booking types are skipped.</small>
                            </td>
                          </tr>
                          <tr data-field="actuals_create_other_assignments_vehicle_types">
                            <td class="alert-label">Other Assignments Vehicle Types</td>
                            <td>
                              <FilterSettingEditor
                                v-model="model.actuals_create_other_assignments_vehicle_types"
                                fieldKey="actuals_create_other_assignments_vehicle_types"
                                variant="wildcard"
                                :allow-raw-edit="isEffectiveAdmin"
                                @validation="onFilterValidation"
                              />
                            </td>
                          </tr>
                          <tr data-field="actuals_create_other_assignments_other_assignment_type_id">
                            <td class="alert-label">Other Assignment Type ID</td>
                            <td>
                              <b-form-input v-model="model.actuals_create_other_assignments_other_assignment_type_id" type="text" :state="numericState('actuals_create_other_assignments_other_assignment_type_id')" @input="validateNumeric('actuals_create_other_assignments_other_assignment_type_id', $event, 1)"></b-form-input>
                              <b-form-invalid-feedback :state="numericState('actuals_create_other_assignments_other_assignment_type_id')">{{ numericFeedback('actuals_create_other_assignments_other_assignment_type_id') }}</b-form-invalid-feedback>
                              <small class="text-muted d-block">Required TBN otherAssignmentTypeID for created records</small>
                            </td>
                          </tr>
                          <tr data-field="actuals_create_other_assignments_seniority_group_id">
                            <td class="alert-label">Other Assignment Seniority Group ID</td>
                            <td>
                              <b-form-input v-model="model.actuals_create_other_assignments_seniority_group_id" type="text" :state="numericState('actuals_create_other_assignments_seniority_group_id')" @input="validateNumeric('actuals_create_other_assignments_seniority_group_id', $event, 1)"></b-form-input>
                              <b-form-invalid-feedback :state="numericState('actuals_create_other_assignments_seniority_group_id')">{{ numericFeedback('actuals_create_other_assignments_seniority_group_id') }}</b-form-invalid-feedback>
                              <small class="text-muted d-block">Optional; sent as null when blank</small>
                            </td>
                          </tr>
                          <tr data-field="actuals_create_other_assignments_pay_rate_group_set_id">
                            <td class="alert-label">Other Assignment Pay Rate Group Set ID</td>
                            <td>
                              <b-form-input v-model="model.actuals_create_other_assignments_pay_rate_group_set_id" type="text" :state="numericState('actuals_create_other_assignments_pay_rate_group_set_id')" @input="validateNumeric('actuals_create_other_assignments_pay_rate_group_set_id', $event, 1)"></b-form-input>
                              <b-form-invalid-feedback :state="numericState('actuals_create_other_assignments_pay_rate_group_set_id')">{{ numericFeedback('actuals_create_other_assignments_pay_rate_group_set_id') }}</b-form-invalid-feedback>
                            </td>
                          </tr>
                          <tr data-field="actuals_create_other_assignments_department_code_id">
                            <td class="alert-label">Other Assignment Department Code ID</td>
                            <td>
                              <b-form-input v-model="model.actuals_create_other_assignments_department_code_id" type="text" :state="numericState('actuals_create_other_assignments_department_code_id')" @input="validateNumeric('actuals_create_other_assignments_department_code_id', $event, 1)"></b-form-input>
                              <b-form-invalid-feedback :state="numericState('actuals_create_other_assignments_department_code_id')">{{ numericFeedback('actuals_create_other_assignments_department_code_id') }}</b-form-invalid-feedback>
                              <small class="text-muted d-block">Optional; sent as null when blank</small>
                            </td>
                          </tr>
                        </SubsectionGroup>

                        <SubsectionGroup v-if="tenant_type === 'CM'" label="Time Calculation (CM)" :visible="!!toggle.grpTimeCalcCm" @toggle="toggle_div(toggle, 'grpTimeCalcCm')">
                          <tr data-field="actuals_use_back_arrival_time">
                            <td class="alert-label">Use Back Arrival Time</td>
                            <td><b-form-select v-model="model.actuals_use_back_arrival_time" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr data-field="actuals_extend_search_range_for_multiday">
                            <td class="alert-label">Extend Search Range For Multiday</td>
                            <td><b-form-select v-model="model.actuals_extend_search_range_for_multiday" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr data-field="actuals_allow_partial_actuals">
                            <td class="alert-label">Allow Partial Actuals</td>
                            <td><b-form-select v-model="model.actuals_allow_partial_actuals" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr data-field="actuals_allow_pickup_dropoff_actuals">
                            <td class="alert-label">Enable Pickup & Dropoff Actuals</td>
                            <td><b-form-select v-model="model.actuals_allow_pickup_dropoff_actuals" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                        </SubsectionGroup>

                        <SubsectionGroup label="Overlap &amp; Validation Checks" :visible="!!toggle.grpOverlapValidation" @toggle="toggle_div(toggle, 'grpOverlapValidation')">
                          <tr v-if="tenant_type=='CM'" data-field="actuals_check_for_adjacent_bookings">
                            <td class="alert-label">Check For Adjacent Bookings</td>
                            <td><b-form-select v-model="model.actuals_check_for_adjacent_bookings" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr data-field="actuals_check_for_overlaps">
                            <td class="alert-label">Check For Overlapping Actuals</td>
                            <td><b-form-select v-model="model.actuals_check_for_overlaps" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr data-field="actuals_check_for_planned_overlaps">
                            <td class="alert-label">Flag Planned Schedule Overlaps</td>
                            <td><b-form-select v-model="model.actuals_check_for_planned_overlaps" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr data-field="actuals_inline_overlap_recheck_enabled">
                            <td class="alert-label">Re-check Overlaps During Reprocess</td>
                            <td><b-form-select v-model="model.actuals_inline_overlap_recheck_enabled" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr v-if="tenant_type=='TBN'" data-field="actuals_check_for_overlapping_driver_logs">
                            <td class="alert-label">Check For Overlapping Driver Logs (School Bus)</td>
                            <td><b-form-select v-model="model.actuals_check_for_overlapping_driver_logs" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                        </SubsectionGroup>

                        <SubsectionGroup label="Telematics &amp; Mileage" :visible="!!toggle.grpTelematicsMileage" @toggle="toggle_div(toggle, 'grpTelematicsMileage')">
                          <tr data-field="actuals_use_eld_geofences">
                            <td class="alert-label">Use Telematics Geofences</td>
                            <td><b-form-select v-model="model.actuals_use_eld_geofences" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr v-if="tenant_type=='TBN'" data-field="actuals_default_geofence_meters">
                            <td class="alert-label">Default Geofence Meters</td>
                            <td>
                              <b-form-input v-model="model.actuals_default_geofence_meters" type="text" :state="numericState('actuals_default_geofence_meters')" @input="validateNumeric('actuals_default_geofence_meters', $event, 1, 100000)"></b-form-input>
                              <b-form-invalid-feedback :state="numericState('actuals_default_geofence_meters')">{{ numericFeedback('actuals_default_geofence_meters') }}</b-form-invalid-feedback>
                            </td>
                          </tr>
                          <tr v-if="tenant_type=='TBN'" data-field="actuals_mileage_disabled">
                            <td class="alert-label">Disable Actuals Mileage</td>
                            <td><b-form-select v-model="model.actuals_mileage_disabled" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr v-if="tenant_type=='CM'" data-field="actuals_supports_live_dead_mileage">
                            <td class="alert-label">Supports Live & Dead Mileage</td>
                            <td><b-form-select v-model="model.actuals_supports_live_dead_mileage" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr v-if="telematics_type == 'SAMSARA'" data-field="actuals_use_dvirs_enabled">
                            <td class="alert-label">Pull DVIR Data</td>
                            <td><b-form-select v-model="model.actuals_use_dvirs_enabled" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr v-if="tenant_type == 'TBN'" data-field="actuals_ignore_missing_segment_locations">
                            <td class="alert-label">Ignore Missing Segment Locations</td>
                            <td><b-form-select v-model="model.actuals_ignore_missing_segment_locations" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr v-if="tenant_type == 'CM'" data-field="actuals_ignore_missing_mileage">
                            <td class="alert-label">Ignore Missing Mileage</td>
                            <td><b-form-select v-model="model.actuals_ignore_missing_mileage" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                          <tr v-if="tenant_type=='TBN'" data-field="actuals_hos_active_statuses">
                            <td class="alert-label">HOS Active Statuses</td>
                            <td><b-form-input v-model="model.actuals_hos_active_statuses" type="text"></b-form-input></td>
                          </tr>
                          <tr v-if="tenant_type=='TBN'" data-field="actuals_prime_locations_and_hos">
                            <td class="alert-label">Prime Locations & HOS</td>
                            <td><b-form-select v-model="model.actuals_prime_locations_and_hos" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                        </SubsectionGroup>

                        <SubsectionGroup label="Notifications &amp; Overrides" :visible="!!toggle.grpNotificationsOverrides" @toggle="toggle_div(toggle, 'grpNotificationsOverrides')">
                          <tr data-field="actuals_email_recipients">
                            <td class="alert-label">Email Recipients</td>
                            <td><b-form-input v-model="model.actuals_email_recipients" type="text"></b-form-input></td>
                          </tr>
                          <tr v-if="tenant_type=='CM'" data-field="actuals_finish_time_offset_config">
                            <td class="alert-label">Finish Time Offset Config</td>
                            <td><b-form-input v-model="model.actuals_finish_time_offset_config" type="text"></b-form-input></td>
                          </tr>
                          <tr v-if="tenant_type=='TBN'" data-field="actuals_force_outbound_enabled">
                            <td class="alert-label">Force Outbound Enabled</td>
                            <td><b-form-select v-model="model.actuals_force_outbound_enabled" :options="boolean_options" style="width:100px"></b-form-select></td>
                          </tr>
                        </SubsectionGroup>
                      </b-card-body>
                    </b-collapse>
                  </b-card>
                </b-card-body>
              </b-collapse>
            </b-card>

          </form>
        </div>
        <div v-else>
          <br/><br/>
          <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
        </div>

      <div class="tc-sticky-save">
        <transition name="tc-save-slide">
        <div v-if="isDirty || saving" class="d-flex align-items-center justify-content-center">
          <span v-if="isDirty && !saving" class="tc-unsaved-label mr-3" @click="scrollToFirstDirtyField()" title="Click to scroll to unsaved field" style="cursor:pointer">&#9679; Unsaved changes</span>
          <b-btn size="lg" variant="success" @click="save_config" :disabled="saving">
          <b-spinner v-if="saving" small class="mr-1"></b-spinner>
          {{ saving ? 'Saving...' : 'Save' }}
        </b-btn>
          &nbsp;&nbsp;
          <b-btn size="lg" variant="secondary" @click="load_data(true)">Discard Changes</b-btn>
        </div>
        </transition>
      </div>
      </div>

    </template>
    <script>
      import {get_tenant_config_settings, update_tenant_config_settings} from '../networking/apis'
      import filterValidationMixin from '../mixins/filter_validation_mixin'
      import {mapState} from "vuex";
      import numericValidationMixin from '../mixins/numeric_validation_mixin'
      import jsonValidationMixin from '../mixins/json_validation_mixin'
      import dirtyTrackingMixin from '../mixins/dirty_tracking_mixin'
      import confirmDefaultsMixin from '../mixins/confirm_defaults_mixin'
      import JsonObjectListEditor from './JsonObjectListEditor.vue'
      import SubsectionGroup from './SubsectionGroup.vue'
      import FilterSettingEditor from './FilterSettingEditor.vue'
      import { ensureFilterKeys } from '../utils/filter_syntax'

      export default {
        name: 'tenant_config',
        mixins: [filterValidationMixin, numericValidationMixin, jsonValidationMixin, dirtyTrackingMixin, confirmDefaultsMixin],
        components: {
          JsonObjectListEditor,
          SubsectionGroup,
          FilterSettingEditor,
        },

        data() {
          return {
            add_yard_model: {},
            manage_sos_yards: false,
            model: {},
            general_model: {},
            general_advanced_model: {},
            toggle: {
              isBookingProcessingVisible: false,
              isBookingProcessingAdvVisible: false,
              isVehicleDriverSyncVisible: false,
              isActualsProcessingVisible: false,
              isActualsProcessingAdvVisible: false,
              grpBookingIncludeExclude: false,
              grpBookingTypeIdFilters: false,
              grpVehicleFilters: false,
              grpBaseFilters: false,
              grpFeedIssueAlerts: false,
              grpProcessingBehavior: false,
              grpBookingRefreshSync: false,
              grpActualsIncludeExclude: false,
              grpProcessingTiming: false,
              grpDisplayViewFilters: false,
              grpOutboundToTbn: false,
              grpActualsV2: false,
              grpTimeCalcTbn: false,
              grpOtherAssignmentsTbn: false,
              grpTimeCalcCm: false,
              grpOverlapValidation: false,
              grpTelematicsMileage: false,
              grpNotificationsOverrides: false,
              grpFleetIntDriverLogins: false,
            },
            summary: {},
            loading: true,
            isNotSysadmin: false,
            isSamaraType: false,
            yardsLength: 0,
            objectKeyList: [],
            boolean_options: [
              { text: 'True', value: 'True' },
              { text: 'False', value: 'False' },
            ],
            auto_verify_columns: [
              { key: 'base', label: 'Base', placeholder: '* for wildcard' },
              { key: 'start_minutes', label: 'Start Minutes', type: 'number', placeholder: 'max diff' },
              { key: 'end_minutes', label: 'End Minutes', type: 'number', placeholder: 'max diff' },
              { key: 'total_minutes', label: 'Total Minutes', type: 'number', placeholder: 'max diff' },
            ],
            base_config_columns: [
              { key: 'base', label: 'Base Name', placeholder: 'e.g. Main Yard' },
              { key: 'bookings', label: 'Bookings', type: 'boolean' },
              { key: 'scheduledservices', label: 'Scheduled Services', type: 'boolean' },
              { key: 'schoolcontract', label: 'School Contract', type: 'boolean' },
            ],
            yard_offset_columns: [
              { key: 'yard_name', label: 'Yard Name', placeholder: 'must match FirstStartBase', width: '180px' },
              { key: 'start_offset_minutes_am', label: 'AM Start Before (mins)', type: 'number', placeholder: '', width: '55px' },
              { key: 'stop_offset_minutes_am', label: 'AM Stop After (mins)', type: 'number', placeholder: '', width: '55px' },
              { key: 'start_offset_minutes_pm', label: 'PM Start Before (mins)', type: 'number', placeholder: '', width: '55px' },
              { key: 'stop_offset_minutes_pm', label: 'PM Stop After (mins)', type: 'number', placeholder: '', width: '55px' },
              { key: 'start_offset_minutes_mid', label: 'MID Start Before (mins)', type: 'number', placeholder: '', width: '55px' },
              { key: 'stop_offset_minutes_mid', label: 'MID Stop After (mins)', type: 'number', placeholder: '', width: '55px' },
              { key: 'start_offset_minutes_late', label: 'LATE Start Before (mins)', type: 'number', placeholder: '', width: '55px' },
              { key: 'stop_offset_minutes_late', label: 'LATE Stop After (mins)', type: 'number', placeholder: '', width: '55px' },
              { key: 'bookings', label: 'Bookings', type: 'boolean', width: '90px' },
              { key: 'scheduledservices', label: 'Scheduled Services', type: 'boolean', width: '90px' },
              { key: 'schoolcontract', label: 'School Contracts', type: 'boolean', width: '90px' },
            ],
            vehicle_type_mapping_columns: [
              { key: 'tag', label: 'Telematics Tag', placeholder: 'e.g. Motor Coach' },
              { key: 'value', label: 'TBN Vehicle Type Name', placeholder: 'e.g. Premium Motorcoach' },
            ],
            vehicle_yard_mapping_columns: [
              { key: 'tag', label: 'Telematics Tag', placeholder: 'e.g. Lake Charles' },
              { key: 'value', label: 'TBN Yard Name', placeholder: 'e.g. Winnie Lot' },
            ],
            fieldPrefixSectionMap: [
              ['actuals_booking_feed_issues_', 'isBookingProcessingVisible'],
              ['booking_flow_', 'isBookingProcessingVisible'],
              ['booking_replace_', 'isBookingProcessingVisible'],
              ['tbn_sync_', 'isVehicleDriverSyncVisible'],
              ['actuals_exclude_non_diary_', 'isBookingProcessingVisible'],
              ['actuals_skip_contract_', 'isBookingProcessingVisible'],
              ['actuals_skip_bookings_without_', 'isBookingProcessingVisible'],
              ['actuals_skip_duplicate_', 'isBookingProcessingVisible'],
              ['actuals_skip_scheduled_', 'isActualsProcessingVisible'],
              ['actuals_excluded_booking_', 'isBookingProcessingVisible'],
              ['actuals_supported_booking_', 'isBookingProcessingVisible'],
              ['actuals_supported_client_', 'isBookingProcessingVisible'],
              ['actuals_refresh_bookings_', 'isBookingProcessingVisible'],
              ['actuals_refresh_booking_', 'isBookingProcessingVisible'],
              ['actuals_force_booking_', 'isBookingProcessingVisible'],
              ['actuals_save_booking_', 'isBookingProcessingVisible'],
              ['actuals_max_days_ago', 'isBookingProcessingVisible'],
              ['actuals_ignore_booking_if_vehicle_', 'isBookingProcessingVisible'],
              ['actuals_client_id_filter', 'isBookingProcessingVisible'],
              ['actuals_contract_id_filter', 'isActualsProcessingVisible'],
              ['actuals_scheduled_services_filter', 'isBookingProcessingVisible'],
              ['actuals_supports_booking_by_vehicle_', 'isBookingProcessingVisible'],
              ['actuals_default_invalid_start_times_', 'isBookingProcessingVisible'],
              ['actuals_driver_hos_', 'isBookingProcessingVisible'],
              ['actuals_vehicle_type_', 'isBookingProcessingVisible'],
              ['actuals_create_other_assignments_', 'isActualsProcessingAdvVisible'],
            ],
            jsonFieldToggleMap: {
              tbn_sync_mismatch_report_recipients: 'isVehicleDriverSyncVisible',
              tbn_sync_excluded_drivers: 'isVehicleDriverSyncVisible',
              tbn_sync_excluded_vehicles: 'isVehicleDriverSyncVisible',
              tbn_sync_vehicle_type_mapping: 'isVehicleDriverSyncVisible',
              tbn_sync_vehicle_type_mapping_default: 'isVehicleDriverSyncVisible',
              tbn_sync_vehicle_yard_mapping: 'isVehicleDriverSyncVisible',
              tbn_sync_vehicle_yard_mapping_default: 'isVehicleDriverSyncVisible',
              actuals_enabled: 'isActualsProcessingVisible',
              actuals_base_config: 'isActualsProcessingVisible',
              actuals_outbound_base_config: 'isActualsProcessingVisible',
              actuals_outbound_auto_verify_config: 'isActualsProcessingVisible',
              actuals_yard_offset_config: 'isActualsProcessingVisible',
              actuals_booking_feed_issues_email_recipients: 'isBookingProcessingVisible',
              actuals_booking_feed_issues_hours_since: 'isBookingProcessingVisible',
              actuals_scheduled_services_filter: 'isBookingProcessingVisible',
              actuals_supported_booking_bases: 'isBookingProcessingVisible',
              actuals_supported_client_ids: 'isBookingProcessingVisible',
              actuals_skip_scheduled_services: 'isActualsProcessingVisible',
              actuals_client_id_filter: 'isBookingProcessingVisible',
              actuals_contract_id_filter: 'isActualsProcessingVisible',
              actuals_include_off_duty_assigned_time: 'isActualsProcessingVisible',
              actuals_fall_back_to_vehicle_times: 'isActualsProcessingVisible',
              actuals_fall_back_to_vehicle_times_scheduled_services: 'isActualsProcessingVisible',
              actuals_use_planned_driver_start_time: 'isActualsProcessingVisible',
              actuals_use_planned_driver_start_time_scheduled_services: 'isActualsProcessingVisible',
              actuals_use_spot_time_as_pickup_time: 'isActualsProcessingVisible',
              actuals_use_depart_time_as_dropoff_time: 'isActualsProcessingVisible',
              actuals_only_live_hours: 'isActualsProcessingVisible',
              actuals_multiday_flat_hours: 'isActualsProcessingVisible',
              actuals_create_other_assignments_between_shifts: 'isActualsProcessingVisible',
              actuals_create_other_assignments_vehicle_types: 'isActualsProcessingVisible',
              actuals_create_other_assignments_other_assignment_type_id: 'isActualsProcessingVisible',
              actuals_create_other_assignments_seniority_group_id: 'isActualsProcessingVisible',
              actuals_create_other_assignments_pay_rate_group_set_id: 'isActualsProcessingVisible',
              actuals_create_other_assignments_department_code_id: 'isActualsProcessingVisible',
              actuals_use_back_arrival_time: 'isActualsProcessingVisible',
              actuals_extend_search_range_for_multiday: 'isActualsProcessingVisible',
              actuals_date_time_format: 'isActualsProcessingVisible',
              actuals_process_days_ago_limit: 'isActualsProcessingVisible',
              actuals_allow_partial_actuals: 'isActualsProcessingVisible',
              actuals_allow_pickup_dropoff_actuals: 'isActualsProcessingVisible',
              actuals_check_for_adjacent_bookings: 'isActualsProcessingVisible',
              actuals_check_for_overlaps: 'isActualsProcessingVisible',
              actuals_check_for_planned_overlaps: 'isActualsProcessingVisible',
              actuals_inline_overlap_recheck_enabled: 'isActualsProcessingVisible',
              actuals_check_for_overlapping_driver_logs: 'isActualsProcessingVisible',
              actuals_mileage_disabled: 'isActualsProcessingVisible',
              actuals_email_recipients: 'isActualsProcessingVisible',
              actuals_finish_time_offset_config: 'isActualsProcessingVisible',
              actuals_outbound_enabled: 'isActualsProcessingVisible',
              actuals_outbound_bookings_disabled: 'isActualsProcessingVisible',
              actuals_outbound_scheduled_services_disabled: 'isActualsProcessingVisible',
              actuals_use_eld_geofences: 'isActualsProcessingVisible',
              actuals_supports_live_dead_mileage: 'isActualsProcessingVisible',
              actuals_filter_bookings_view_by_company: 'isActualsProcessingVisible',
              actuals_use_dvirs_enabled: 'isActualsProcessingVisible',
              actuals_ignore_missing_segment_locations: 'isActualsProcessingVisible',
              actuals_ignore_missing_mileage: 'isActualsProcessingVisible',
              actuals_default_geofence_meters: 'isActualsProcessingVisible',
              actuals_hos_active_statuses: 'isActualsProcessingVisible',
              actuals_prime_locations_and_hos: 'isActualsProcessingVisible',
              actuals_force_outbound_enabled: 'isActualsProcessingVisible',
              actuals_processing_delay_in_minutes: 'isActualsProcessingVisible',
              actuals_process_by_yard: 'isActualsProcessingVisible',
              actuals_hide_vehicle_missing_errors_web: 'isActualsProcessingVisible',
              actuals_vehicle_type_filter: 'isBookingProcessingVisible',
              actuals_ignore_booking_if_vehicle_is_missing: 'isBookingProcessingVisible',
            },
            fieldAdvancedSectionMap: {
              booking_replace_with_eld_values: 'isBookingProcessingAdvVisible',
              actuals_skip_duplicate_booking_updates: 'isBookingProcessingAdvVisible',
              actuals_supports_booking_by_vehicle_company: 'isBookingProcessingAdvVisible',
              actuals_default_invalid_start_times_enabled: 'isBookingProcessingAdvVisible',
              actuals_driver_hos_required_enabled: 'isBookingProcessingAdvVisible',
              actuals_max_days_ago: 'isBookingProcessingAdvVisible',
              actuals_force_booking_updates_enabled: 'isBookingProcessingAdvVisible',
              actuals_refresh_bookings_disabled: 'isBookingProcessingAdvVisible',
              actuals_refresh_bookings_nightly_disabled: 'isBookingProcessingAdvVisible',
              actuals_refresh_booking_days_in_future: 'isBookingProcessingAdvVisible',
              actuals_refresh_bookings_minutes_past_the_hour: 'isBookingProcessingAdvVisible',
              tbn_sync_drivers_skip: 'isVehicleDriverSyncVisible',
              tbn_sync_drivers_basic_enabled: 'isVehicleDriverSyncVisible',
              tbn_sync_vehicles_skip: 'isVehicleDriverSyncVisible',
              tbn_sync_add_eld_vehicles: 'isVehicleDriverSyncVisible',
              actuals_include_off_duty_assigned_time: 'isActualsProcessingAdvVisible',
              actuals_v2_enabled: 'isActualsProcessingAdvVisible',
              actuals_v2_shadow_enabled: 'isActualsProcessingAdvVisible',
              actuals_v2_cutover_date: 'isActualsProcessingAdvVisible',
              actuals_v2_reprocess_bypass_cutover: 'isActualsProcessingAdvVisible',
              actuals_fall_back_to_vehicle_times: 'isActualsProcessingAdvVisible',
              actuals_fall_back_to_vehicle_times_scheduled_services: 'isActualsProcessingAdvVisible',
              actuals_use_planned_driver_start_time: 'isActualsProcessingAdvVisible',
              actuals_use_planned_driver_start_time_scheduled_services: 'isActualsProcessingAdvVisible',
              actuals_use_spot_time_as_pickup_time: 'isActualsProcessingAdvVisible',
              actuals_use_depart_time_as_dropoff_time: 'isActualsProcessingAdvVisible',
              actuals_only_live_hours: 'isActualsProcessingAdvVisible',
              actuals_multiday_flat_hours: 'isActualsProcessingAdvVisible',
              actuals_create_other_assignments_between_shifts: 'isActualsProcessingAdvVisible',
              actuals_create_other_assignments_vehicle_types: 'isActualsProcessingAdvVisible',
              actuals_create_other_assignments_other_assignment_type_id: 'isActualsProcessingAdvVisible',
              actuals_create_other_assignments_seniority_group_id: 'isActualsProcessingAdvVisible',
              actuals_create_other_assignments_pay_rate_group_set_id: 'isActualsProcessingAdvVisible',
              actuals_create_other_assignments_department_code_id: 'isActualsProcessingAdvVisible',
              actuals_hos_planned_start_max_hours: 'isActualsProcessingAdvVisible',
              actuals_hos_planned_finish_max_hours: 'isActualsProcessingAdvVisible',
              actuals_use_back_arrival_time: 'isActualsProcessingAdvVisible',
              actuals_extend_search_range_for_multiday: 'isActualsProcessingAdvVisible',
              actuals_allow_partial_actuals: 'isActualsProcessingAdvVisible',
              actuals_allow_pickup_dropoff_actuals: 'isActualsProcessingAdvVisible',
              actuals_check_for_adjacent_bookings: 'isActualsProcessingAdvVisible',
              actuals_check_for_overlaps: 'isActualsProcessingAdvVisible',
              actuals_check_for_planned_overlaps: 'isActualsProcessingAdvVisible',
              actuals_inline_overlap_recheck_enabled: 'isActualsProcessingAdvVisible',
              actuals_check_for_overlapping_driver_logs: 'isActualsProcessingAdvVisible',
              actuals_mileage_disabled: 'isActualsProcessingAdvVisible',
              actuals_email_recipients: 'isActualsProcessingAdvVisible',
              actuals_finish_time_offset_config: 'isActualsProcessingAdvVisible',
              actuals_use_eld_geofences: 'isActualsProcessingAdvVisible',
              actuals_supports_live_dead_mileage: 'isActualsProcessingAdvVisible',
              actuals_use_dvirs_enabled: 'isActualsProcessingAdvVisible',
              actuals_ignore_missing_segment_locations: 'isActualsProcessingAdvVisible',
              actuals_ignore_missing_mileage: 'isActualsProcessingAdvVisible',
              actuals_default_geofence_meters: 'isActualsProcessingAdvVisible',
              actuals_hos_active_statuses: 'isActualsProcessingAdvVisible',
              actuals_prime_locations_and_hos: 'isActualsProcessingAdvVisible',
              actuals_force_outbound_enabled: 'isActualsProcessingAdvVisible',
            },
            fieldGroupMap: {
              actuals_skip_contract_bookings: 'grpBookingIncludeExclude',
              actuals_exclude_non_diary_bookings: 'grpBookingIncludeExclude',
              actuals_skip_bookings_without_vehicles: 'grpBookingIncludeExclude',
              actuals_skip_scheduled_services: 'grpActualsIncludeExclude',
              actuals_ignore_booking_if_vehicle_is_missing: 'grpBookingIncludeExclude',
              actuals_excluded_booking_types: 'grpBookingTypeIdFilters',
              actuals_scheduled_services_filter: 'grpBookingTypeIdFilters',
              actuals_client_id_filter: 'grpBookingTypeIdFilters',
              actuals_supported_client_ids: 'grpBookingTypeIdFilters',
              actuals_contract_id_filter: 'grpActualsIncludeExclude',
              actuals_excluded_booking_vehicles: 'grpVehicleFilters',
              actuals_vehicle_type_filter: 'grpVehicleFilters',
              actuals_excluded_booking_bases: 'grpBaseFilters',
              actuals_supported_booking_bases: 'grpBaseFilters',
              actuals_base_config: 'grpActualsIncludeExclude',
              actuals_booking_feed_issues_email_recipients: 'grpFeedIssueAlerts',
              actuals_booking_feed_issues_hours_since: 'grpFeedIssueAlerts',
              booking_flow_skip_privates: 'grpFeedIssueAlerts',
              booking_flow_skip_contracts: 'grpFeedIssueAlerts',
              booking_replace_with_eld_values: 'grpProcessingBehavior',
              actuals_skip_duplicate_booking_updates: 'grpProcessingBehavior',
              actuals_supports_booking_by_vehicle_company: 'grpProcessingBehavior',
              actuals_default_invalid_start_times_enabled: 'grpProcessingBehavior',
              actuals_driver_hos_required_enabled: 'grpProcessingBehavior',
              actuals_max_days_ago: 'grpProcessingBehavior',
              actuals_force_booking_updates_enabled: 'grpBookingRefreshSync',
              actuals_refresh_bookings_disabled: 'grpBookingRefreshSync',
              actuals_refresh_bookings_nightly_disabled: 'grpBookingRefreshSync',
              actuals_refresh_booking_days_in_future: 'grpBookingRefreshSync',
              actuals_refresh_bookings_minutes_past_the_hour: 'grpBookingRefreshSync',
              actuals_processing_delay_in_minutes: 'grpProcessingTiming',
              actuals_process_by_yard: 'grpProcessingTiming',
              actuals_process_days_ago_limit: 'grpProcessingTiming',
              actuals_hide_vehicle_missing_errors_web: 'grpDisplayViewFilters',
              actuals_filter_bookings_view_by_company: 'grpDisplayViewFilters',
              actuals_date_time_format: 'grpDisplayViewFilters',
              actuals_outbound_enabled: 'grpOutboundToTbn',
              actuals_outbound_base_config: 'grpOutboundToTbn',
              actuals_outbound_auto_verify_config: 'grpOutboundToTbn',
              actuals_outbound_bookings_disabled: 'grpOutboundToTbn',
              actuals_outbound_scheduled_services_disabled: 'grpOutboundToTbn',
              actuals_yard_offset_config: 'grpOutboundToTbn',
              actuals_v2_enabled: 'grpActualsV2',
              actuals_v2_shadow_enabled: 'grpActualsV2',
              actuals_v2_cutover_date: 'grpActualsV2',
              actuals_v2_reprocess_bypass_cutover: 'grpActualsV2',
              actuals_hos_planned_start_max_hours: 'grpActualsV2',
              actuals_hos_planned_finish_max_hours: 'grpActualsV2',
              actuals_include_off_duty_assigned_time: 'grpTimeCalcTbn',
              actuals_fall_back_to_vehicle_times: 'grpTimeCalcTbn',
              actuals_use_spot_time_as_pickup_time: 'grpTimeCalcTbn',
              actuals_use_depart_time_as_dropoff_time: 'grpTimeCalcTbn',
              actuals_fall_back_to_vehicle_times_scheduled_services: 'grpTimeCalcTbn',
              actuals_use_planned_driver_start_time: 'grpTimeCalcTbn',
              actuals_use_planned_driver_start_time_scheduled_services: 'grpTimeCalcTbn',
              actuals_only_live_hours: 'grpTimeCalcTbn',
              actuals_multiday_flat_hours: 'grpTimeCalcTbn',
              actuals_create_other_assignments_between_shifts: 'grpOtherAssignmentsTbn',
              actuals_create_other_assignments_vehicle_types: 'grpOtherAssignmentsTbn',
              actuals_create_other_assignments_other_assignment_type_id: 'grpOtherAssignmentsTbn',
              actuals_create_other_assignments_seniority_group_id: 'grpOtherAssignmentsTbn',
              actuals_create_other_assignments_pay_rate_group_set_id: 'grpOtherAssignmentsTbn',
              actuals_create_other_assignments_department_code_id: 'grpOtherAssignmentsTbn',
              actuals_use_back_arrival_time: 'grpTimeCalcCm',
              actuals_extend_search_range_for_multiday: 'grpTimeCalcCm',
              actuals_allow_partial_actuals: 'grpTimeCalcCm',
              actuals_allow_pickup_dropoff_actuals: 'grpTimeCalcCm',
              actuals_check_for_adjacent_bookings: 'grpOverlapValidation',
              actuals_check_for_overlaps: 'grpOverlapValidation',
              actuals_check_for_planned_overlaps: 'grpOverlapValidation',
              actuals_inline_overlap_recheck_enabled: 'grpOverlapValidation',
              actuals_check_for_overlapping_driver_logs: 'grpOverlapValidation',
              actuals_use_eld_geofences: 'grpTelematicsMileage',
              actuals_default_geofence_meters: 'grpTelematicsMileage',
              actuals_mileage_disabled: 'grpTelematicsMileage',
              actuals_supports_live_dead_mileage: 'grpTelematicsMileage',
              actuals_use_dvirs_enabled: 'grpTelematicsMileage',
              actuals_ignore_missing_segment_locations: 'grpTelematicsMileage',
              actuals_ignore_missing_mileage: 'grpTelematicsMileage',
              actuals_hos_active_statuses: 'grpTelematicsMileage',
              actuals_prime_locations_and_hos: 'grpTelematicsMileage',
              actuals_email_recipients: 'grpNotificationsOverrides',
              actuals_finish_time_offset_config: 'grpNotificationsOverrides',
              actuals_force_outbound_enabled: 'grpNotificationsOverrides',
              tbn_sync_fleetint_drivers_enabled: 'grpFleetIntDriverLogins',
              tbn_sync_fleetint_drivers_employment_statuses: 'grpFleetIntDriverLogins',
              tbn_sync_fleetint_drivers_required_qualifications: 'grpFleetIntDriverLogins',
              tbn_sync_fleetint_drivers_remove_unqualified: 'grpFleetIntDriverLogins',
              tbn_sync_fleetint_drivers_company_names: 'grpFleetIntDriverLogins',
            },
          }
        },
        async mounted() {
          this.load_data()
        },
        methods: {
          async load_data(silent = false) {
            if (!silent) this.loading = true
            this.objectKeyList = []
            let loaded = false
            try {
              const {value: { data = [] } } = await get_tenant_config_settings(this.user.tenant)
              this.summary = data.summary
              this.model = data.results
              ensureFilterKeys(this.model, [
                'actuals_excluded_booking_types',
                'actuals_client_id_filter',
                'actuals_contract_id_filter',
                'actuals_excluded_booking_vehicles',
                'actuals_vehicle_type_filter',
                'actuals_excluded_booking_bases',
                'actuals_create_other_assignments_vehicle_types',
              ])
              if (!('ftp_enabled' in this.model)) this.$set(this.model, 'ftp_enabled', '')

              try {
                const entries = Object.entries(this.model);
                for (let [key, value] of entries) {
                  if (this.isArrayType(value) || this.isPlainObject(value, key)) {
                    let setting = JSON.stringify(value)
                    this.model[key] = setting
                    this.objectKeyList.push(key)
                  }
                }
              } catch (e) {
                console.log('error looping through keys', e)
              }
              loaded = true
            } catch(e) {
              this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
            }

            this.loading = false
            if (loaded) this._resetDirtyTracking()
          },
          async save_config() {
            try {
              this.saving = true
              this.flushNumericValidation()
              this.flushFilterValidation()
              if (this.hasNumericErrors()) {
                this.$emit('showAlert', 'Fix the highlighted field before saving', 'danger', 6000)
                this.scrollToFirstNumericError()
                return
              }
              if (this.hasFilterErrors()) {
                this.$emit('showAlert', 'Fix the highlighted filter field before saving', 'danger', 6000)
                this.scrollToFirstFilterError()
                return
              }
              if (this.hasJsonErrors()) {
                this.$emit('showAlert', 'Invalid JSON in field: ' + this.firstJsonErrorKey() + ' — ' + this.jsonFeedback(this.firstJsonErrorKey()), 'danger', 8000)
                this.expandAndScrollToJsonError()
                return
              }
              // Compute dirty keys before parsing (model still holds JSON strings matching snapshot).
              const dirtyKeys = new Set(Object.keys(this._dirtyFields()))
              if (dirtyKeys.size === 0) {
                this.$emit('showAlert', 'No changes to save', 'info', 3000)
                return
              }

              const fullPayload = Object.assign({}, this.model)
              for (const objkey of this.objectKeyList) {
                try {
                  if (fullPayload[objkey] && fullPayload[objkey].trim && fullPayload[objkey].trim() !== '') {
                    fullPayload[objkey] = JSON.parse(fullPayload[objkey])
                  }
                } catch (e) {
                  this.$emit('showAlert', 'Invalid JSON in field: ' + objkey + ' — ' + e.message, 'danger', 8000)
                  this.expandAndScrollToJsonError()
                  this.load_data()
                  return
                }
              }
              const payload = {}
              for (const key of dirtyKeys) {
                if (key in fullPayload) payload[key] = fullPayload[key]
              }

              this.$emit('showAlert', 'Saving Changes', 'info',5000)
              await update_tenant_config_settings(this.user.tenant, payload)
              this._resetDirtyTracking()
              await this.load_data(true)
              this.$emit('showAlert', 'Configuration Saved', 'success',5000)
            } catch(e) {
              this.$emit('showAlert', this._saveErrorMessage(e), 'danger', 5000)
            }
            finally {
              this.saving = false
            }
          },
          set_tenant_id(id) {
            this.user.tenant = id
            this.load_data()
          },
          apply_actuals_defaults() {
            this.model.actuals_enabled = 'True'
            this.model.actuals_v2_enabled = 'True'
            this.model.actuals_use_eld_geofences = 'True'
            this.model.actuals_use_dvirs_enabled = 'True'
          },
          update_toggle_div(item,field,setting) {
            if (!item[field] && setting) {
              item[field] = !item[field];
              this.$forceUpdate();
            } else if (item[field] && !setting) {
              item[field] = !item[field];
              this.$forceUpdate();
            }
          },
          toggle_div(item,field) {
            item[field] = !item[field];
            this.$forceUpdate();
          },
          scrollToFirstDirtyField() {
            const field = this._firstDirtyField()
            if (!field) return

            let toggleKey = null
            if (this.jsonFieldToggleMap && this.jsonFieldToggleMap[field]) {
              toggleKey = this.jsonFieldToggleMap[field]
            }
            if (!toggleKey && this.fieldPrefixSectionMap) {
              for (const [prefix, key] of this.fieldPrefixSectionMap) {
                if (field.startsWith(prefix) || field === prefix) {
                  toggleKey = key
                  break
                }
              }
            }
            if (!toggleKey && field.startsWith('actuals_')) {
              toggleKey = 'isActualsProcessingVisible'
            }

            const advKey = this.fieldAdvancedSectionMap && this.fieldAdvancedSectionMap[field]
            const groupKey = this.fieldGroupMap && this.fieldGroupMap[field]
            const sectionWasClosed = toggleKey && this.toggle && !this.toggle[toggleKey]
            const advWasClosed = advKey && this.toggle && !this.toggle[advKey]
            const groupWasClosed = groupKey && this.toggle && !this.toggle[groupKey]

            if (toggleKey && this.toggle !== undefined) {
              this.$set(this.toggle, toggleKey, true)
            }
            if (advKey && this.toggle !== undefined) {
              this.$set(this.toggle, advKey, true)
            }
            if (groupKey && this.toggle !== undefined) {
              this.$set(this.toggle, groupKey, true)
            }

            const delay = (sectionWasClosed || advWasClosed || groupWasClosed) ? 420 : 0
            this.$nextTick(() => setTimeout(() => this._scrollToFieldElement(field), delay))
          },
          expandAndScrollToJsonError() {
            const fieldKey = this.firstJsonErrorKey()
            if (!fieldKey) return false

            const toggleKey = this.jsonFieldToggleMap[fieldKey]
            const advKey = this.fieldAdvancedSectionMap && this.fieldAdvancedSectionMap[fieldKey]
            const groupKey = this.fieldGroupMap && this.fieldGroupMap[fieldKey]
            const wasClosed = [toggleKey, advKey, groupKey].some(key => key && this.toggle && !this.toggle[key])

            for (const key of [toggleKey, advKey, groupKey]) {
              if (key && this.toggle !== undefined) {
                this.$set(this.toggle, key, true)
              }
            }

            const delay = wasClosed ? 420 : 0
            this.$nextTick(() => setTimeout(() => {
              const el = this.$el.querySelector(`[data-json-field="${fieldKey}"]`)
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                el.classList.remove('tc-error-highlight')
                void el.offsetWidth
                el.classList.add('tc-error-highlight')
                el.addEventListener('animationend', () => el.classList.remove('tc-error-highlight'), { once: true })
              }
            }, delay))
            return true
          },
          _scrollToFieldElement(field) {
            const row = field ? this.$el.querySelector(`[data-field="${field}"]`) : null
            const jsonEl = field ? this.$el.querySelector(`[data-json-field="${field}"]`) : null

            let scrollTarget = this.$el
            if (row) {
              scrollTarget = row
            } else if (jsonEl) {
              scrollTarget = jsonEl
            }

            scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' })

            let flashEl
            if (scrollTarget.tagName === 'TR') {
              const jsonEditor = scrollTarget.querySelector('[data-json-field]')
              flashEl = jsonEditor || scrollTarget.querySelector('input, select, textarea, .custom-select') || scrollTarget
            } else {
              flashEl = scrollTarget
            }

            flashEl.classList.remove('tc-error-highlight')
            void flashEl.offsetWidth
            flashEl.classList.add('tc-error-highlight')
            flashEl.addEventListener('animationend', () => flashEl.classList.remove('tc-error-highlight'), { once: true })
          },
          isPlainObject(value, key) {
            let objtype = (typeof value)
            if (objtype == "object") {
              if (value != null && !Array.isArray(value)) {
                return true
              }
              return false
            }
          },
          isArrayType(value) {
            return Array.isArray(value)
          },
        } ,
        computed:{
          ...mapState({
            user: state => state.profile.user,
            viewAs: state => state.profile.view_as,
          }),
          isEffectiveAdmin() {
            return this.user.role === 'admin' && this.viewAs !== 'non-admin'
          },
          telematics_type() {
            return this.user.telematics_types[this.user.tenant]
          },
          tenant_type() {
            return this.user.types[this.user.tenant]
          }
        },
        watch: {
          user: {
            deep: true,
            handler() {
              this.load_data()
            }
          }
        }
      }
    </script>
    <style>
      @import "../../node_modules/@syncfusion/ej2-base/styles/bootstrap5.css";
      @import "../../node_modules/@syncfusion/ej2-inputs/styles/bootstrap5.css";
      @import "../../node_modules/@syncfusion/ej2-lists/styles/bootstrap5.css";
      @import "../../node_modules/@syncfusion/ej2-popups/styles/bootstrap5.css";
      @import "../../node_modules/@syncfusion/ej2-buttons/styles/bootstrap5.css";
      @import "../../node_modules/@syncfusion/ej2-navigations/styles/bootstrap5.css";
      @import "../../node_modules/@syncfusion/ej2-splitbuttons/styles/bootstrap5.css";
      @import "../../node_modules/@syncfusion/ej2-richtexteditor/styles/bootstrap5.css";

      .alerts-global-row {
        padding: 10px 0;
        font-size: 18px;
        font-weight: bold;
      }
      .alerts-global-label {
        min-width: 220px;
      }
      .alert-section-card {
        border: 1px solid #dee2e6;
        border-radius: 4px;
      }
      .alert-section-header {
        cursor: pointer;
        user-select: none;
        background-color: #f8f9fa;
        padding: 10px 16px;
        font-size: 16px;
        font-weight: 600;
        color: #333;
        border-bottom: 1px solid #dee2e6;
      }
      .alert-section-header:hover {
        background-color: #e9ecef;
      }
      .alert-section-title {
        color: var(--fi-rail-accent);
        font-size: 17px;
      }
      .alert-chevron {
        display: inline-block;
        font-size: 12px;
        color: #666;
        transition: transform 0.2s ease;
      }
      .alert-chevron.open {
        transform: rotate(90deg);
      }
      .alert-settings-table td {
        vertical-align: middle;
        padding: 6px 10px;
      }
      .alert-settings-table td.alert-label {
        text-align: right;
        width: 340px;
        font-weight: 500;
        color: #444;
        white-space: nowrap;
      }
      .alert-settings-table td:not(.alert-label) {
        text-align: left;
      }
    </style>
